/**
 * Third-party vendor loaders.
 *
 * Intercom, Hotjar and the Facebook pixel are loaded here directly, with no
 * tag manager or analytics middleware in between. Two entry points: stubs that
 * have to exist synchronously, and network loads that must stay off the
 * critical path.
 *
 * Google tags are not loaded here. GA4 (G-CPE8GFJWC1) and Google Ads
 * (AW-10780691895) come from __partials/footer.html on marketing pages and
 * __partials/analytics_gtag.html on the signup page.
 */

var INTERCOM_APP_ID = 'kkvkbk66';
var HOTJAR_SITE_ID = 2295188;
var FB_PIXEL_ID = '1019460525483085';

function injectScript(src) {
  var script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = src;

  var first = document.getElementsByTagName('script')[0];
  if (first && first.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    (document.head || document.documentElement).appendChild(script);
  }

  return script;
}

/* ------------------------------------------------------------------ *
 * Intercom
 * ------------------------------------------------------------------ */

/**
 * Installs the Intercom queue stub synchronously, at import time.
 *
 * This matters beyond tidiness: several pages carry inline
 * `href="Javascript:Intercom('show')"` handlers, and shared/layout.js and
 * __partials/email-capture-popup.html both poll for `window.Intercom` in a
 * retry loop. Without the stub, an early click throws a ReferenceError and the
 * pollers spin. With it present from the first line of the bundle, every one
 * of those calls queues and replays the moment the widget arrives.
 */
function installIntercomStub() {
  if (window.Intercom) return;

  window.intercomSettings = window.intercomSettings || {};
  window.intercomSettings.app_id = INTERCOM_APP_ID;

  var stub = function () {
    stub.c(arguments);
  };
  stub.q = [];
  stub.c = function (args) {
    stub.q.push(args);
  };

  window.Intercom = stub;
}

var intercomWidgetLoaded = false;
function loadIntercomWidget() {
  if (intercomWidgetLoaded) return;
  intercomWidgetLoaded = true;

  installIntercomStub();
  injectScript('https://widget.intercom.io/widget/' + INTERCOM_APP_ID);
}

/**
 * True only once the real widget has booted.
 *
 * `window.Intercom` is truthy from the moment the bundle runs, because the
 * queue stub is installed at import time, so a plain `if (window.Intercom)`
 * check is always true and is NOT a readiness test. Callers that have a
 * fallback (the mailto: links on the enterprise CTAs, /contact's form, the
 * navbar's /contact link) must use this instead, or a visitor with
 * widget.intercom.io blocked by an ad blocker or corporate DNS would have
 * their message pushed into a queue nothing ever drains, with no fallback and
 * no error.
 */
function isIntercomReady() {
  return !!(window.Intercom && !window.Intercom.q);
}

/* ------------------------------------------------------------------ *
 * Hotjar
 * ------------------------------------------------------------------ */

var hotjarLoaded = false;
function loadHotjar() {
  if (hotjarLoaded) return;
  hotjarLoaded = true;

  window.hj =
    window.hj ||
    function () {
      (window.hj.q = window.hj.q || []).push(arguments);
    };
  window._hjSettings = { hjid: HOTJAR_SITE_ID, hjsv: 6 };

  injectScript(
    'https://static.hotjar.com/c/hotjar-' +
      window._hjSettings.hjid +
      '.js?sv=' +
      window._hjSettings.hjsv
  );
}

/* ------------------------------------------------------------------ *
 * Facebook Pixel
 * ------------------------------------------------------------------ */

var fbPixelLoaded = false;
function loadFacebookPixel() {
  if (fbPixelLoaded) return;
  fbPixelLoaded = true;

  if (!window.fbq) {
    var fbq = function () {
      fbq.callMethod
        ? fbq.callMethod.apply(fbq, arguments)
        : fbq.queue.push(arguments);
    };

    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;

    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];

    injectScript('https://connect.facebook.net/en_US/fbevents.js');
  }

  window.fbq('init', FB_PIXEL_ID);
  window.fbq('track', 'PageView');
}

/* ------------------------------------------------------------------ *
 * Entry points
 * ------------------------------------------------------------------ */

/**
 * Runs synchronously at import time. Costs nothing on the network, it only
 * defines the queues that inline handlers and pollers depend on.
 */
function installVendorStubs() {
  installIntercomStub();
}

/**
 * Intercom, loaded eagerly on idle so the chat launcher is present for every
 * visitor. Gating it behind a mouseover or touchstart would leave scroll-only
 * and keyboard-only visitors without a chat bubble at all.
 */
function loadEagerVendors() {
  loadIntercomWidget();
}

/**
 * Marketing tags, still gated behind the first interaction. These have no
 * bearing on what the visitor can do on the page, so they stay off the
 * critical path to protect INP.
 */
function loadDeferredVendors() {
  loadHotjar();
  loadFacebookPixel();
}

module.exports = {
  installVendorStubs: installVendorStubs,
  loadEagerVendors: loadEagerVendors,
  loadDeferredVendors: loadDeferredVendors,
  loadIntercomWidget: loadIntercomWidget,
  isIntercomReady: isIntercomReady,
  INTERCOM_APP_ID: INTERCOM_APP_ID,
};
